/**
 * 회의록 API (v2.118.0 · 2026-09-03)
 *
 * ⭐ 목록·상세·결재는 **지출결재와 같은 통로**(expense-api)를 쓴다.
 *   여기 있는 것은 「회의록에만 있는 것」 — 작성·수정뿐이다.
 */

import type { ApiResponse } from './types';
import type { RichDoc } from './rich-doc';
import { apiErrorText } from './api-error-text';

const BASE = '/api';

/** 참석자 한 명 — 사내(계정 있음) 또는 외부(소속 직접 입력) */
export interface MeetingAttendee {
  seq: number;
  userId: string | null;
  empId: string | null;
  name: string;
  title: string | null;
  department: string | null;
  /** 외부 참석자 소속 (사내는 빈 값) */
  org: string;
  isExternal: boolean;
}

export interface MeetingActionItem {
  seq: number;
  task: string;
  owner: string;
  /** 'YYYY-MM-DD' · 안 정했으면 null */
  dueDate: string | null;
}

/**
 * 저장된 본문 그림의 주소. **로그인 쿠키가 함께 가야** 열린다(같은 출처라 `<img>` 로 충분).
 * 서버는 이 주소에서도 문서 열람 권한을 그대로 확인한다.
 */
export function meetingImageUrl(docId: number, imageId: number): string {
  return `${BASE}/expense/meeting/${docId}/image/${imageId}`;
}

export interface MeetingDetail {
  title: string;
  meetingDate: string;
  startTime: string;
  endTime: string;
  place: string;
  agenda: string;
  /** 논의 내용의 **글자만** (서버가 문서에서 만든 사본 · 표·그림 제외) — 본문은 discussionDoc 에 있다 */
  discussion: string;
  decision: string;
  remark: string;
  /**
   * 논의 내용 — 서식 있는 문서 (v2.124.0). 저장된 그림은 번호(imageId)만 있다 —
   *   편집기에 넣기 전에 `docForEdit` 로 볼 수 있는 주소를 붙인다.
   */
  discussionDoc: RichDoc;
  /** 결정 사항 — 서식 있는 문서 (v2.124.0) */
  decisionDoc: RichDoc;
  attendees: MeetingAttendee[];
  actionItems: MeetingActionItem[];
  /** 서버가 계산한 회의 시간(분). 시각을 안 적었거나 앞뒤가 안 맞으면 null */
  meetingMinutes: number | null;
  /** 「1시간 30분」 */
  meetingTimeText: string;
}

export interface MeetingSaveInput {
  title: string;
  meetingDate: string;
  startTime: string;
  endTime: string;
  place: string;
  agenda: string;
  /**
   * ⚠**보내지 않는다** — 서버가 `discussionDoc` 에서 글자를 다시 만든다.
   *   빈 문자열로 두는 이유: 서버 zod 가 아직 이 칸을 받고, 여기서 값을 채우면
   *   블록과 어긋난 사본이 저장될 수 있다(서버가 블록을 이기게 해 뒀지만 헷갈릴 자리를 없앤다).
   */
  discussion: string;
  decision: string;
  remark: string;
  /**
   * 논의 내용 — 서식 있는 문서 (v2.124.0). **`docForSave` 를 거친 것**을 보낸다:
   *   새 그림은 `dataUrl`, 저장된 그림은 번호만. 화면 표시용 주소(src)는 서버가 버린다.
   */
  discussionDoc: RichDoc;
  decisionDoc: RichDoc;
  /** 사내 참석자 계정 — 작성자 본인은 서버가 1번으로 넣는다 */
  internalIds: string[];
  externals: { name: string; org: string; title: string }[];
  actionItems: { task: string; owner: string; dueDate: string }[];
  docDate?: string;
  submit: boolean;
}

/** 화면·서버가 같이 지키는 상한 (서버 lib/approval/meeting.ts 와 같은 값) */
export const MAX_ATTENDEES = 20;
export const MAX_ACTION_ITEMS = 20;

/* ── 본문 그림 상한 (v2.122.0) ─────────────────────────────────
 * ⚠서버 `lib/approval/meeting-image.ts` 에 같은 값이 또 있다. 둘을 같이 고칠 것.
 * ⭐긴 변 1600px 은 **A4 에 인쇄될 때 충분한 해상도**다. 그보다 큰 그림은 화면이 줄여서
 *   보낸다 — 줄이는 김에 캔버스로 다시 그리므로 원본 파일에 붙어 있던 것도 함께 사라진다.
 */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
export const MAX_IMAGE_LONG_EDGE = 1600;
/** 상한(표 칸 수·그림 수 …)은 `lib/rich-doc.ts` 의 MEETING_DOC_LIMITS 에 있다 */

async function send<T>(path: string, method: 'POST' | 'PUT', body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(apiErrorText(json.error));   // 어느 칸이 왜 틀렸는지까지
  return json.data;
}

export function createMeetingDoc(
  input: MeetingSaveInput,
): Promise<{ id: number; docNo: string; status: string }> {
  return send('/expense/meeting', 'POST', input);
}

/**
 * 수정 — 서명을 받는 단계에서 **내용이 바뀌었으면**(changed) 받은 서명이 무효가 된다(voided = 몇 건) (v2.229.0).
 */
export function updateMeetingDoc(
  id: number,
  input: MeetingSaveInput,
): Promise<{ id: number; status: string; changed?: boolean; voided?: number }> {
  return send(`/expense/meeting/${id}`, 'PUT', input);
}

/* ── 동의 서명 · 변경 요청 (v2.229.0) ───────────────────────────── */

/** 본문이 있을 때만 content-type 을 붙인다 — 본문 없는 POST 에 붙이면 서버가 400 을 낸다 */
async function act<T>(path: string, method: 'POST' | 'DELETE', body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(apiErrorText(json.error));
  return json.data;
}

/** 동의 서명 — 화면이 **읽은 내용의 지문**을 함께 보낸다. 그 사이 내용이 바뀌었으면 서버가 거절한다 */
export function signMeeting(
  id: number,
  contentHash: string,
): Promise<{ status: string; signed: number; required: number; complete: boolean; becameComplete: boolean }> {
  return act(`/expense/meeting/${id}/sign`, 'POST', { contentHash });
}

/** 서명 거두기 — 서명 중일 때만 */
export function unsignMeeting(id: number): Promise<{ id: number; status: string }> {
  return act(`/expense/meeting/${id}/unsign`, 'POST');
}

/** 변경 요청(참석자) · 답글(작성자) — 글자만 */
export function addMeetingComment(id: number, body: string): Promise<{ id: number }> {
  return act(`/expense/meeting/${id}/comments`, 'POST', { body });
}

/** 변경 요청 「반영함」 켜고 끄기 */
export function resolveMeetingComment(id: number, cid: number, resolved: boolean): Promise<{ id: number; resolved: boolean }> {
  return act(`/expense/meeting/${id}/comments/${cid}/resolve`, 'POST', { resolved });
}

/** 내가 쓴 글 지우기 */
export function deleteMeetingComment(id: number, cid: number): Promise<{ id: number; deleted: boolean }> {
  return act(`/expense/meeting/${id}/comments/${cid}`, 'DELETE');
}

/**
 * 회의록 상태 이름 (v2.229.0) — 상태 칸은 결재 엔진의 칸을 그대로 쓰고 **뜻만 다르다**:
 *   작성중 = 임시저장 · 검토대기 = 서명 중 · 최종승인 = 서명 완료.
 * legacy = 결재로 끝난 옛 회의록(2026-09 결재 방식) — 「결재 완료」 그대로 부른다.
 */
export function meetingStatusLabel(status: string, legacy: boolean): string {
  if (legacy) return '결재 완료';
  switch (status) {
    case '작성중': return '작성중';
    case '검토대기': return '서명 중';
    case '최종승인': return '서명 완료';
    // 옛 결재 흐름의 반려 — 마이그레이션이 작성중으로 옮긴다(남아 있으면 임시저장처럼 다룬다)
    case '반려': return '작성중';
    default: return status;
  }
}

/** 「1시간 30분」 — 화면이 입력 중에 미리 보여 줄 때 (서버 계산과 같은 식). */
export function localMeetingTimeText(startTime: string, endTime: string): string {
  const t = (s: string): number | null => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const s = t(startTime);
  const e = t(endTime);
  if (s === null || e === null) return '';
  const min = e - s;
  if (min <= 0) return '';
  const h = Math.floor(min / 60);
  const mm = min % 60;
  if (h > 0 && mm > 0) return `${h}시간 ${mm}분`;
  if (h > 0) return `${h}시간`;
  return `${mm}분`;
}
